import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {truncate} from 'common/util/string';

import {template} from './template';
import css from './index.styl?inline';

type FileUploaderAttrs = {
  label: string;
  accept?: string;
  icon?: string;
  'processing-description'?: string;
  'completed-filename'?: string;
  'max-filename-chars'?: number;
  'enable-delete'?: boolean;
  disabled?: boolean;
};

type FileUploaderJsxProps = {
  attrs: FileUploaderAttrs;
};

/** File upload component with 3 main states:
 * 1) a button to begin the upload (proxies the underlying input's `change` event on file selected)
 * 2) a spinner with label, shown when attr `processing-description` is set
 * 3) a filename label with optional delete (trashcan) button, shown when `completed-filename` attr is set
 *  */
export class FileUploader extends Component<EmptyObj, FileUploaderAttrs> {
  static get attrsSchema(): AttrsSchema<FileUploaderAttrs> {
    return {
      label: {type: `string`, description: `Label for the upload button`},
      accept: {type: `string`, default: `text/csv`},
      icon: {
        type: `string`,
        default: `export-csv`,
        description: `Icon for the upload button and file label. Empty string omits icon.`,
      },
      'processing-description': {
        type: `string`,
        default: ``,
        description: `Set this attr to show a spinner along with this description. Ignored if 'completed-filename' is set.`,
      },
      'completed-filename': {
        type: `string`,
        default: ``,
        description: `Set this attr when the selection / upload is complete.`,
      },
      'max-filename-chars': {type: `number`, default: 60, description: `Truncates displayed 'completed-filename'`},
      'enable-delete': {
        type: `boolean`,
        description: `Show delete button next to the selected file name that emits 'delete' event on click`,
      },
      disabled: `boolean`,
    };
  }

  uploadButtonRef: HTMLElement;
  deleteButtonRef: HTMLElement;
  focusCallback;

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute(`tabindex`, `0`);
    this.focusCallback = () => {
      if (this.uploadButtonRef) {
        requestAnimationFrame(() => {
          this.uploadButtonRef.focus();
        });
      } else if (this.deleteButtonRef) {
        requestAnimationFrame(() => this.deleteButtonRef.focus());
      }
    };
    this.addEventListener(`focus`, this.focusCallback);
  }
  disconnectedCallback() {
    this.removeEventListener(`focus`, this.focusCallback);
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {},
      helpers: {
        uploadButtonInserted: ({elm}) => {
          this.uploadButtonRef = elm;
        },
        deleteButtonInserted: ({elm}) => {
          this.deleteButtonRef = elm;
        },
        uploadButtonDestroyed: () => {
          this.uploadButtonRef = null;
        },
        deleteButtonDestroyed: () => {
          this.deleteButtonRef = null;
        },
        getFileLabel: () => {
          return truncate(this.attr(`completed-filename`), this.attr(`max-filename-chars`));
        },
        getFileTooltip: () => {
          const fullFilename = this.attr(`completed-filename`);
          return fullFilename.length > this.attr(`max-filename-chars`) ? fullFilename : null;
        },
        handleChange: (ev) => {
          const file = ev.currentTarget.files[0];
          if (file) {
            this.dispatchEvent(new CustomEvent(`change`, {detail: {file}}));
          }
        },
        handleDelete: () => {
          this.dispatchEvent(new CustomEvent(`delete`));
        },
      },
    };
  }
}

export const FileUploaderEl = defineAndMakeJsxFactory<FileUploaderJsxProps>(`mp-file-uploader`, FileUploader);
